import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React, {useState} from 'react'
import styled, {css} from 'styled-components'

import Card from '@/components/card'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import {ProofPointBadges} from '@/components/proof-point-badges/proof-point-badges'
import SEO from '@/components/seo'
import type {PerksInternalPageQuery} from '@/graphql-types'
import TitleWithAccordion from '@/sections/title-with-accordion'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Main} from '@helios/shared/components/skip-to-content'
import SmoothScrollLink from '@helios/shared/components/smooth-scroll-link'
import {Section, BackgroundWrapper, Layout} from '@helios/shared/styles/grid'
import {TextLink} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PerksInternalPageQuery>

const PerksInternalPage = (props: Props) => {
    const [showMore, setShowMore] = useState(false)

    const data = props.data.allPrismicPerksInternalPage.nodes[0].data

    if (!data) {
        return handleNoPrismicData()
    }

    const toggleShowMore = () => {
        setShowMore((showMore) => !showMore)
    }

    return (
        <>
            <Header />

            <Main>
                <Section padding="small">
                    <FeatureBlock
                        image={data.hero[0].image}
                        title={data.hero[0].title}
                        body={data.hero[0].body.richText}
                        titleVariant="heading-large"
                    >
                        <Button as={SmoothScrollLink} selector="#perks" verticalAlignment="start">
                            {data.hero[0].cta_text}
                        </Button>

                        {!!data.hero[0].proof_point_badges?.document && (
                            <ProofPointBadges
                                proofPoints={[
                                    data.hero[0].proof_point_badges.document.data
                                        .proof_point_first[0],
                                    data.hero[0].proof_point_badges.document.data
                                        .proof_point_second[0],
                                ]}
                            />
                        )}
                    </FeatureBlock>
                </Section>

                {data.feature_blocks.map((block, i) => (
                    <Section key={`${block.title}-${i}`}>
                        <FeatureBlock
                            image={block.image}
                            title={block.title}
                            body={block.body.richText}
                            mirrored={!(i % 2)}
                        />
                    </Section>
                ))}

                <BackgroundWrapper color="muted" id="perks">
                    <CardsSection>
                        <Layout
                            as={CardsGrid}
                            showMore={showMore}
                            variant="four-equal"
                            alignItems="stretch"
                        >
                            {data.perks.map(({perk}) => (
                                <Card
                                    key={perk.document.id}
                                    ctaText={data.perk_card_cta_text}
                                    title={perk.document.data.company_name}
                                    body={perk.document.data.perk_details}
                                    image={perk.document.data.logo}
                                    ctaLink={perk.document.data.link}
                                />
                            ))}

                            {data.perks.length > 6 && (
                                <ShowMore onClick={toggleShowMore}>
                                    {data.show_more_perks_text}
                                </ShowMore>
                            )}
                        </Layout>
                    </CardsSection>
                </BackgroundWrapper>

                {data.perks_partner.map((block, i) => (
                    <BackgroundWrapper color="yellow" key={`${block.title}-${i}`}>
                        <Section>
                            <FeatureBlock
                                image={block.image}
                                title={block.title}
                                body={block.body.richText}
                            >
                                <Button variant="outline" as={PrismicLink} {...block.cta_link}>
                                    {block.cta_text}
                                </Button>
                            </FeatureBlock>
                        </Section>
                    </BackgroundWrapper>
                ))}

                <TitleWithAccordion
                    title={data.questions_title}
                    items={data.questions_answers?.map((item) => ({
                        title: item.question,
                        body: item.answer?.richText,
                    }))}
                />
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PerksInternalPageQuery>) => {
    const pageData = data.allPrismicPerksInternalPage.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const CardsSection = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing60};
`

interface CardsGridProps {
    showMore?: boolean
}

const ShowMore = styled(TextLink).attrs({as: 'button'})`
    grid-column: 1 / -1;
    justify-self: center;
`

const truncateCards = css`
    ${mq.max('md')} {
        ${ShowMore} {
            display: block;
        }

        > :nth-child(n + 7):not(${ShowMore}) {
            display: none;
        }
    }
`

const CardsGrid = styled.div<CardsGridProps>`
    ${ShowMore} {
        display: none;
    }

    ${(props) => !props.showMore && truncateCards}
`

export const query = graphql`
    query PerksInternalPage($locale: String!) {
        allPrismicPerksInternalPage(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    hero {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                        cta_text
                        proof_point_badges {
                            document {
                                ... on PrismicProofPointBadgesRepeatable {
                                    data {
                                        proof_point_first {
                                            title
                                            subtitle
                                            icon {
                                                alt
                                                copyright
                                                url
                                                dimensions {
                                                    height
                                                    width
                                                }
                                            }
                                        }
                                        proof_point_second {
                                            title
                                            subtitle
                                            icon {
                                                alt
                                                copyright
                                                url
                                                dimensions {
                                                    height
                                                    width
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    feature_blocks {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                    }
                    perk_card_cta_text
                    show_more_perks_text
                    perks {
                        perk {
                            document {
                                ... on PrismicPerk {
                                    id
                                    data {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                        company_name
                                        perk_details
                                        link {
                                            ...PrismicLink
                                        }
                                    }
                                }
                            }
                        }
                    }
                    perks_partner {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                        cta_text
                        cta_link {
                            ...PrismicLink
                        }
                    }
                    questions_title
                    questions_answers {
                        question
                        answer {
                            richText
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(PerksInternalPage)
